// Attendance policy → "Where it applies", built for big departments (100s of
// programmes, 1000s of classrooms). Same idea as cloud consoles that set
// policies over a hierarchy: a navigator on the left, the selected level on
// the right, and for every classroom the policy actually IN EFFECT and where
// it comes from — so exceptions stand out without opening anything.
//
//   left   the department, then its programmes (search, filter: all / with
//          their own policy / enforced); each shows its policy at a glance
//   right  the selected level's own policy + enforce, what is in effect and
//          why, then its classrooms — searched and paged on the server
//          (25 / 50 / 100 a page), tick several and set or clear their
//          policy in one go
//
// Precedence (same as the server's resolver): the first ENFORCED level from
// the top wins; otherwise the most specific (classroom → programme →
// department); otherwise no policy (today's behaviour).
import { useEffect, useMemo, useRef, useState } from "react";
import { Building2, ChevronRight, GraduationCap, Layers, Loader2, Lock, School, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DataPager } from "@/components/DataPager";
import { cn } from "@/lib/utils";
import { useInstituteLabels } from "@/lib/instituteLabels";
import type { Assignment, Policy } from "./policyApi";
import { type ClassroomSummary, type DepartmentProfile, type Programme, listDepartmentClassrooms } from "./structureApi";

export const INHERIT = "__inherit__";

export type AssignChange = { level: Assignment[ "level" ]; targetId: string; policyKey: string; enforced: boolean };

type Props = {
    department: DepartmentProfile;
    deptName: string;
    programmes: Programme[];
    policies: Policy[];
    assignments: Assignment[];
    policyName: (key: string) => string;
    busy: boolean;
    onAssign: (changes: AssignChange[]) => Promise<void>;
};

type Scope = { kind: "department" } | { kind: "programme"; id: string };
type Source = "department" | "programme" | "classroom" | "none";
type Effective = { policyKey: string | null; source: Source; enforced: boolean };

const PAGE_SIZES = [ 25, 50, 100 ];

export function PolicyScopeBrowser({ department, deptName, programmes, policies, assignments, policyName, busy, onAssign }: Props) {
    const L = useInstituteLabels();
    const prog = L.programme.toLowerCase();
    const dept = L.department.toLowerCase();

    const [ scope, setScope ] = useState<Scope>({ kind: "department" });
    const [ navQuery, setNavQuery ] = useState("");
    const [ navFilter, setNavFilter ] = useState<"all" | "own" | "enforced">("all");

    // ── who has what ────────────────────────────────────────────────────────
    const byTarget = useMemo(() => {
        const m = new Map<string, Assignment>();
        for (const a of assignments) m.set(`${a.level}:${a.targetId}`, a);
        return m;
    }, [ assignments ]);
    const own = (level: Assignment[ "level" ], id: string | null | undefined) => (id ? byTarget.get(`${level}:${id}`) || null : null);
    const deptA = own("department", department.departmentName);

    const effective = (programmeId: string | null, classroomId: string | null): Effective => {
        const chain = [
            { source: "department" as const, a: deptA },
            { source: "programme" as const, a: own("programme", programmeId) },
            { source: "classroom" as const, a: own("classroom", classroomId) },
        ].filter(c => c.a);
        const enforced = chain.find(c => c.a!.enforced && c.source !== "classroom");
        const pick = enforced || chain[ chain.length - 1 ];
        return pick ? { policyKey: pick.a!.policyKey, source: pick.source, enforced: !!enforced } : { policyKey: null, source: "none", enforced: false };
    };

    // ── navigator ───────────────────────────────────────────────────────────
    const navProgrammes = useMemo(() => {
        const q = navQuery.trim().toLowerCase();
        return programmes.filter(p => {
            if (q && !p.name.toLowerCase().includes(q)) return false;
            const a = byTarget.get(`programme:${p.id}`);
            if (navFilter === "own") return !!a;
            if (navFilter === "enforced") return !!a?.enforced;
            return true;
        });
    }, [ programmes, navQuery, navFilter, byTarget ]);

    const selectedProgramme = scope.kind === "programme" ? programmes.find(p => p.id === scope.id) || null : null;
    useEffect(() => { if (scope.kind === "programme" && !selectedProgramme) setScope({ kind: "department" }); }, [ scope, selectedProgramme ]);

    // ── classrooms of the selected scope, paged on the server ───────────────
    const [ classQuery, setClassQuery ] = useState("");
    const [ page, setPage ] = useState(1);
    const [ pageSize, setPageSize ] = useState(25);
    const [ rows, setRows ] = useState<ClassroomSummary[]>([]);
    const [ total, setTotal ] = useState(0);
    const [ loading, setLoading ] = useState(false);
    const [ picked, setPicked ] = useState<Set<string>>(new Set());
    const [ bulkPolicy, setBulkPolicy ] = useState<string>("");
    const seq = useRef(0);
    const programmeFilter = scope.kind === "programme" ? scope.id : null;

    useEffect(() => { setPage(1); setPicked(new Set()); }, [ classQuery, programmeFilter, pageSize ]);
    useEffect(() => {
        const mine = ++seq.current;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const res = await listDepartmentClassrooms(department.departmentName, classQuery.trim(), page, pageSize, programmeFilter);
                if (mine === seq.current) { setRows(res.classrooms); setTotal(res.total); }
            } catch {
                if (mine === seq.current) { setRows([]); setTotal(0); }
            } finally {
                if (mine === seq.current) setLoading(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [ department.departmentName, classQuery, page, pageSize, programmeFilter ]);

    const programmeName = (id: string | null) => (id ? programmes.find(p => p.id === id)?.name ?? null : null);

    // ── small pieces ─────────────────────────────────────────────────────────
    const sourceLabel = (s: Source) => (s === "department" ? L.department : s === "programme" ? L.programme : s === "classroom" ? "Own" : "");
    const effectiveBadge = (e: Effective) => e.policyKey ? (
        <div className="flex flex-col items-start gap-0.5">
            <span className="flex items-center gap-1 text-sm font-medium">
                {e.enforced && <Lock className="h-3 w-3 text-amber-600" />}{policyName(e.policyKey)}
            </span>
            <span className="text-[11px] text-muted-foreground">from {sourceLabel(e.source).toLowerCase()}{e.enforced ? " (enforced)" : ""}</span>
        </div>
    ) : <span className="text-sm text-muted-foreground">No policy (as today)</span>;

    const policySelect = (value: string, onChange: (v: string) => void, inheritLabel: string, disabled?: boolean, className?: string) => (
        <Select value={value} disabled={busy || disabled} onValueChange={onChange}>
            <SelectTrigger className={cn("h-8 text-xs", className)}><SelectValue /></SelectTrigger>
            <SelectContent>
                <SelectItem value={INHERIT}>{inheritLabel}</SelectItem>
                {policies.map(p => <SelectItem key={p.policyKey} value={p.policyKey}>{policyName(p.policyKey)}</SelectItem>)}
            </SelectContent>
        </Select>
    );

    const navItem = (active: boolean, onClick: () => void, icon: React.ReactNode, title: string, a: Assignment | null, sub?: string) => (
        <button type="button" onClick={onClick}
            className={cn("flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors",
                active ? "bg-primary/10 ring-1 ring-primary/30" : "hover:bg-muted/60")}>
            <span className="shrink-0 text-muted-foreground">{icon}</span>
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{title}</span>
                <span className="block truncate text-[11px] text-muted-foreground">
                    {a ? <>{a.enforced && <Lock className="mr-1 inline h-3 w-3 text-amber-600" />}{policyName(a.policyKey)}</> : (sub ?? "Inherits")}
                </span>
            </span>
            <ChevronRight className={cn("h-4 w-4 shrink-0", active ? "text-primary" : "text-muted-foreground/40")} />
        </button>
    );

    // ── the selected level's own policy ─────────────────────────────────────
    const level = scope.kind === "department" ? "department" : "programme";
    const levelId = scope.kind === "department" ? department.departmentName : scope.id;
    const levelA = own(level, levelId);
    const lockedByDept = level === "programme" && !!deptA?.enforced;
    const levelEffective = scope.kind === "department" ? effective(null, null) : effective(scope.id, null);
    const levelTitle = scope.kind === "department" ? deptName : selectedProgramme?.name ?? "";

    const setLevelPolicy = (v: string) => onAssign([ { level, targetId: levelId, policyKey: v, enforced: v === INHERIT ? false : !!levelA?.enforced } ]);
    const setLevelEnforced = (on: boolean) => levelA && onAssign([ { level, targetId: levelId, policyKey: levelA.policyKey, enforced: on } ]);

    // ── bulk ─────────────────────────────────────────────────────────────────
    const lockedFor = (c: ClassroomSummary) => !!deptA?.enforced || !!own("programme", c.programmeId)?.enforced;
    const pickable = rows.filter(c => !lockedFor(c));
    const allPicked = pickable.length > 0 && pickable.every(c => picked.has(c.id));
    const togglePick = (id: string) => setPicked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const applyBulk = async (policyKey: string) => {
        await onAssign([ ...picked ].map(id => ({ level: "classroom" as const, targetId: id, policyKey, enforced: false })));
        setPicked(new Set());
        setBulkPolicy("");
    };

    return (
        <div className="grid h-full min-h-0 gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
            {/* ── NAVIGATOR ─────────────────────────────────────────────── */}
            <aside className="flex min-h-0 flex-col rounded-lg border bg-muted/10 lg:h-full">
                <div className="space-y-2 border-b p-3">
                    {navItem(scope.kind === "department", () => setScope({ kind: "department" }), <Building2 className="h-4 w-4" />, deptName, deptA, "No policy (as today)")}
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input className="h-8 pl-8 text-sm" placeholder={`Search ${L.programmes.toLowerCase()}…`} value={navQuery} onChange={(e) => setNavQuery(e.target.value)} />
                    </div>
                    <div className="flex gap-1">
                        {([ [ "all", "All" ], [ "own", "Own policy" ], [ "enforced", "Enforced" ] ] as const).map(([ v, label ]) => (
                            <Button key={v} size="sm" variant={navFilter === v ? "default" : "ghost"} className="h-7 px-2 text-xs" onClick={() => setNavFilter(v)}>{label}</Button>
                        ))}
                    </div>
                </div>
                <div className="flex items-center justify-between px-3 pt-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <span className="flex items-center gap-1"><GraduationCap className="h-3.5 w-3.5" /> {L.programmes}</span>
                    <span>{navProgrammes.length}{navProgrammes.length !== programmes.length ? ` of ${programmes.length}` : ""}</span>
                </div>
                <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2 max-lg:max-h-64">
                    {navProgrammes.length === 0 ? (
                        <p className="p-4 text-center text-xs text-muted-foreground">
                            {programmes.length === 0 ? `No ${L.programmes.toLowerCase()} in this ${dept}.` : "Nothing matches."}
                        </p>
                    ) : navProgrammes.map(p => (
                        <div key={p.id}>
                            {navItem(scope.kind === "programme" && scope.id === p.id, () => setScope({ kind: "programme", id: p.id }),
                                <GraduationCap className="h-4 w-4" />, p.name, own("programme", p.id))}
                        </div>
                    ))}
                </div>
            </aside>

            {/* ── SELECTED LEVEL ────────────────────────────────────────── */}
            <section className="flex min-h-0 min-w-0 flex-col gap-4">
                {/* breadcrumb + the level's own policy */}
                <div className="rounded-lg border p-4">
                    <div className="mb-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        <button type="button" className="hover:text-foreground hover:underline" onClick={() => setScope({ kind: "department" })}>{deptName}</button>
                        {scope.kind === "programme" && <><ChevronRight className="h-3 w-3" /><span className="text-foreground">{levelTitle}</span></>}
                    </div>
                    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                        <div className="space-y-2">
                            <p className="flex items-center gap-2 text-sm font-semibold">
                                {scope.kind === "department" ? <Building2 className="h-4 w-4 text-primary" /> : <GraduationCap className="h-4 w-4 text-primary" />}
                                Policy for this {scope.kind === "department" ? dept : prog}
                            </p>
                            {lockedByDept ? (
                                <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5 text-amber-600" /> Locked: the {dept}'s policy is enforced</p>
                            ) : (
                                <div className="flex flex-wrap items-center gap-3">
                                    {policySelect(levelA?.policyKey ?? INHERIT, setLevelPolicy,
                                        scope.kind === "department" ? "No policy (as today)" : `Same as the ${dept}`, false, "w-72")}
                                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <Switch checked={!!levelA?.enforced} disabled={!levelA || busy} onCheckedChange={setLevelEnforced} />
                                        Enforce on everything below
                                    </label>
                                    {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                                </div>
                            )}
                        </div>
                        <div className="space-y-2 rounded-md bg-muted/30 p-3">
                            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Layers className="h-3.5 w-3.5" /> In effect here</p>
                            {effectiveBadge(levelEffective)}
                            <p className="text-[11px] text-muted-foreground">
                                First enforced level from the top wins; otherwise the most specific ({L.classroom.toLowerCase()} → {prog} → {dept}).
                            </p>
                        </div>
                    </div>
                </div>

                {/* classrooms */}
                <div className="flex min-h-0 flex-1 flex-col rounded-lg border">
                    <div className="flex flex-col gap-2 border-b p-3 md:flex-row md:items-center md:justify-between">
                        <p className="flex items-center gap-2 text-sm font-semibold">
                            <School className="h-4 w-4 text-primary" />
                            {L.classrooms} {scope.kind === "programme" ? `in ${levelTitle}` : `in the whole ${dept}`}
                            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">{total}</Badge>
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="relative w-64">
                                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input className="h-8 pl-8 text-sm" placeholder="Search by name or ID…" value={classQuery} onChange={(e) => setClassQuery(e.target.value)} />
                                {classQuery && (
                                    <button type="button" aria-label="Clear search" onClick={() => setClassQuery("")}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                                )}
                            </div>
                            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                                <SelectTrigger className="h-8 w-28 text-xs"><SelectValue>{pageSize} / page</SelectValue></SelectTrigger>
                                <SelectContent>{PAGE_SIZES.map(n => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>

                    {picked.size > 0 && (
                        <div className="flex flex-wrap items-center gap-3 border-b bg-primary/5 px-3 py-2 text-sm">
                            <span className="font-medium">{picked.size} selected</span>
                            <Select value={bulkPolicy} onValueChange={setBulkPolicy}>
                                <SelectTrigger className="h-8 w-64 text-xs"><SelectValue placeholder="Give them a policy…" /></SelectTrigger>
                                <SelectContent>{policies.map(p => <SelectItem key={p.policyKey} value={p.policyKey}>{policyName(p.policyKey)}</SelectItem>)}</SelectContent>
                            </Select>
                            <Button size="sm" className="h-8" disabled={!bulkPolicy || busy} onClick={() => applyBulk(bulkPolicy)}>Apply</Button>
                            <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => applyBulk(INHERIT)}>Clear own policy</Button>
                            <Button size="sm" variant="ghost" className="h-8" onClick={() => setPicked(new Set())}>Cancel</Button>
                            {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                        </div>
                    )}

                    <div className={cn("min-h-0 flex-1 overflow-auto", loading && rows.length > 0 && "opacity-60")}>
                        {loading && rows.length === 0 ? (
                            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                        ) : rows.length === 0 ? (
                            <p className="py-16 text-center text-sm text-muted-foreground">
                                {classQuery ? `No ${L.classrooms.toLowerCase()} match “${classQuery}”.` : `No ${L.classrooms.toLowerCase()} here.`}
                            </p>
                        ) : (
                            <Table>
                                <TableHeader className="sticky top-0 z-10 bg-card">
                                    <TableRow className="bg-muted/30">
                                        <TableHead className="w-10 pl-3">
                                            <Checkbox checked={allPicked} disabled={pickable.length === 0 || busy} aria-label="Select every classroom on this page"
                                                onCheckedChange={() => setPicked(prev => {
                                                    const n = new Set(prev);
                                                    if (allPicked) pickable.forEach(c => n.delete(c.id)); else pickable.forEach(c => n.add(c.id));
                                                    return n;
                                                })} />
                                        </TableHead>
                                        <TableHead>{L.classroom}</TableHead>
                                        {scope.kind === "department" && <TableHead>{L.programme}</TableHead>}
                                        <TableHead className="w-72">Own policy</TableHead>
                                        <TableHead className="pr-4">In effect</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.map(c => {
                                        const a = own("classroom", c.id);
                                        const locked = lockedFor(c);
                                        const eff = effective(c.programmeId, c.id);
                                        return (
                                            <TableRow key={c.id} className={cn(a && "bg-amber-50/40")}>
                                                <TableCell className="pl-3">
                                                    <Checkbox checked={picked.has(c.id)} disabled={locked || busy} onCheckedChange={() => togglePick(c.id)} />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="font-medium">{c.name}</div>
                                                    <div className="font-mono text-[11px] text-muted-foreground">{c.id}</div>
                                                </TableCell>
                                                {scope.kind === "department" && (
                                                    <TableCell className="text-sm">
                                                        {c.programmeId ? (
                                                            <button type="button" className="text-left hover:text-primary hover:underline"
                                                                onClick={() => setScope({ kind: "programme", id: c.programmeId! })}>
                                                                {programmeName(c.programmeId) ?? "—"}
                                                            </button>
                                                        ) : <span className="text-muted-foreground">—</span>}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    {locked ? (
                                                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5 text-amber-600" /> Locked from above</span>
                                                    ) : policySelect(a?.policyKey ?? INHERIT,
                                                        (v) => onAssign([ { level: "classroom", targetId: c.id, policyKey: v, enforced: false } ]),
                                                        `Same as above (${prog} / ${dept})`)}
                                                </TableCell>
                                                <TableCell className="pr-4">{effectiveBadge(eff)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                    <DataPager className="border-t px-3 py-2" page={page} pageSize={pageSize} total={total} onPage={setPage} loading={loading} />
                </div>
            </section>
        </div>
    );
}
